/**
 * Client-intake submission schema — the shared validation contract for the public
 * "Financial Snapshot" intake (docs/25-advisor-firm-channel.md §3). Consumed by:
 *   - the façade's public `POST /intake/{firmSlug}` (the security boundary: parse
 *     with this BEFORE anything else touches the body), and
 *   - the SPA wizard (client-side validation of the same shape).
 *
 * Hardening posture (docs/25 security checklist §1) — this schema IS most of it:
 *   - every string is bounded; every row array is capped at {@link MAX_INTAKE_ROWS}. ⚠️ Since
 *     zod 4.6.5 a string bound counts CODE POINTS, not UTF-16 units (observed 2026-10-05,
 *     steward-app#2064; under 4.4.3 an emoji counted as 2), so a `.max(N)` string can be up to
 *     2N units long; and `z.iso.datetime()` refuses an instant without seconds. Both are pinned
 *     in `advisorFirmDirectory.test.ts`; `__proto__` below is not the only verdict that moved;
 *   - every select is enum-pinned (`z.enum`), so option tampering is a 400. The
 *     wizard (S4) renders EXACTLY these enum values — the schema is the source of
 *     truth for the option lists, and a copy change moves both together;
 *   - `strictObject` throughout: an undeclared key anywhere is a rejection, not a
 *     silent strip — a public endpoint should not carry unvalidated freight. That
 *     includes `__proto__` on a JSON-parsed object, as of zod 4.6.5 (observed 2026-10-05,
 *     steward-app#2064; pinned by the `__proto__` tests in `intakeSchema.test.ts` and in
 *     the façade's claim, draft save/removal, link and upload route suites, and no other). It is reported as `unrecognized_keys` like any other
 *     undeclared key, and `Object.prototype` is not polluted. ⚠️ HISTORY, because a
 *     reader of an older revision will remember the opposite: under zod 4.4.3 (the
 *     security review of steward-app#607) the same key was silently DROPPED instead, so
 *     the same body was accepted. A schema-level `__proto__` refusal is a property of the
 *     installed zod, not of this file, which is why the tests pin it rather than this prose;
 *   - the façade additionally enforces {@link INTAKE_MAX_BODY_BYTES} on the raw body
 *     BEFORE parsing, and drops (with the same 202) any submission whose
 *     {@link INTAKE_HONEYPOT_FIELD} is non-empty.
 *
 * Dollar amounts are deliberately bounded STRINGS, not numbers: the RIPL form asks
 * for rough figures ("~$20k/mo", "about 2M") and this is a conversational intake an
 * advisor reads, not ledger data anything computes on. Rejecting a prospective
 * client's submission over number formatting would be the worst trade available.
 *
 * Naming rule (docs/25): "Financial Snapshot" is UI copy only; identifiers say intake.
 */

import { z } from 'zod';
import { INTAKE_ROW_ID_KEY, IntakeRowIdSchema } from './intakeRowId.js';

/** Max repeatable rows per "+ Add …" list. */
export const MAX_INTAKE_ROWS = 20;

/** Raw request-body cap the façade enforces before parsing. */
export const INTAKE_MAX_BODY_BYTES = 64 * 1024;

/**
 * Honeypot: a field real users never see (visually hidden by the wizard) and bots
 * autofill. Named like a legitimate field on purpose. The schema ACCEPTS it — the
 * façade checks it and silently drops the submission (still 202) when non-empty,
 * so a bot learns nothing from the response. The façade must also STRIP the field
 * (empty or not) before enqueue, so it never reaches the stored intake.
 */
export const INTAKE_HONEYPOT_FIELD = 'website' as const;

/**
 * The two code points SQS will not carry, and the reason every free-text field below is
 * charset-bounded as well as length-bounded (security review of steward-app#607).
 *
 * SQS's `MessageBody` accepts only the XML 1.0 character set: `#x9 | #xA | #xD |
 * #x20-#xD7FF | #xE000-#xFFFD | #x10000-#x10FFFF`. Almost everything outside it survives
 * the trip anyway because `JSON.stringify` escapes it — C0 controls come out as six-character
 * escape sequences, and lone surrogates have been escaped since ES2019's well-formed stringify.
 * U+FFFE and U+FFFF are the exact gap: legal in a JSON string, passed through RAW by
 * `JSON.stringify`, and refused by SQS. Written as escapes, never as literal characters —
 * a source file containing them is one an editor or a diff tool can silently mangle.
 *
 * Without this, one such character anywhere in a submission produced an
 * `InvalidMessageContents` throw, a 503, and an ERROR log line — every single time, so a
 * family who pasted one from a badly-encoded document could never submit, no matter how
 * often the copy told them to try again in a few minutes. It also handed an anonymous
 * caller a way to generate ERROR lines on demand at the full route rate, which is the
 * shape of hazard steward-app#410 exists to remember.
 *
 * REJECTED rather than stripped, so the wizard (which validates against this same schema)
 * points at the offending field before the fifteen minutes of typing are spent, instead of
 * silently altering what someone wrote about their own finances.
 */
const SQS_SAFE = /^[^\uFFFE\uFFFF]*$/u;

const short = z.string().trim().max(200).regex(SQS_SAFE);
const amount = z.string().trim().max(100).regex(SQS_SAFE);
const longText = z.string().trim().max(5000).regex(SQS_SAFE);

/** Multi-selects reject repeated values (checkbox UIs cannot produce them). */
const uniqueItems = (arr: readonly string[]) => new Set(arr).size === arr.length;

const person = z.strictObject({
  firstName: short.optional(),
  lastName: short.optional(),
  email: z.email().max(254).optional(),
  phone: short.optional(),
  employer: short.optional(),
  title: short.optional(),
});

/**
 * Whether a value carries no meaningful content: an absent or blank string, an
 * unticked (`false`) boolean, an empty array, or an object every one of whose OWN
 * fields is empty by this same rule. Recurses without needing to know the schema's
 * shape — every field in {@link IntakeSubmissionSchema} is one of exactly these kinds.
 *
 * Exported (2026-09-19, steward-app#1568) so `sectionHasAnswer` in `intakeDraft.ts`
 * can be defined as its exact negation instead of carrying a second, hand-maintained
 * copy of the same recursion. The two call sites differ (this one gates a fully
 * shaped `IntakeSubmissionSchema` object at `.refine()` time; the other counts
 * per-section answers on an unvalidated draft `sections` blob straight off a
 * DynamoDB row) but the "does this value carry anything" rule underneath is the same
 * question asked twice, and a fork here is exactly the kind of drift review flagged
 * on this branch — see `intakeDraft.ts`'s docblock on `sectionHasAnswer` for the
 * numeric/`NaN` edge case this negation preserves.
 */
export function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'boolean') return !value;
  if (Array.isArray(value)) return value.every(isEmptyValue);
  // The row id is bookkeeping, never content (intakeRowId.ts): a row holding only it is blank.
  if (typeof value === 'object') {
    return Object.entries(value).every(([key, v]) => key === INTAKE_ROW_ID_KEY || isEmptyValue(v));
  }
  return false;
}

/**
 * The one guard that survives making every individual field optional below (docs/
 * evidence/ripl-intake-form — RIPL's own form requires nothing on any of its eleven
 * sections, and ours now matches that). It is deliberately NOT a per-field rule: it
 * is "did this submission contain anything at all", asked once, of the whole object.
 * Without it, an all-optional form would let a client post a wholly empty row into
 * the firm's workspace on every "Next, Next, Next, Send" click without typing a
 * single character.
 *
 * The honeypot is included in the check on purpose, not excluded: a submission whose
 * ONLY non-blank field is the honeypot is exactly as "empty" as one with nothing at
 * all filled in, so it fails here and 400s before the façade ever reaches its
 * honeypot branch — the same outcome a genuinely empty body gets either way, so this
 * creates no oracle a bot could use to learn its fill was detected. A honeypot filled
 * ALONGSIDE a real answer is "not empty" by this rule and proceeds normally, where
 * the façade's own honeypot check (handlers/intake.ts) drops it with the usual 202.
 */
function isSubmissionEmpty(data: Record<string, unknown>): boolean {
  // The four Getting started answers (crossmarc/steward-app#2190) are routing, not content: a
  // submission holding nothing but Yes and No answers has told the firm nothing, so they do not
  // count here. Free text in Did we miss anything? does count, like any other answer.
  const { gettingStarted: _routing, ...content } = data;
  return isEmptyValue(content);
}

/**
 * `aboutYou` without its `email`, for deciding whether an INVITED session has answered
 * anything (crossmarc/steward-app#1758). On an invited session the address is normally the
 * writer's stamp off the invitation row rather than something the person typed, so it is not
 * counted as an answer. A value that is not a plain object comes back untouched.
 *
 * Only this STRIPPER is shared, by the browser (`src/intake/draft.ts`) and the writer
 * (`services/intake-writer/src/draft.ts`). Each side decides separately whether a session is
 * invited, and they order the work differently: the browser strips before its only parse, the
 * writer parses the stored sections in full and then parses a stripped copy for emptiness. Used
 * for the emptiness decision only: callers keep storing and sending the unstripped submission.
 */
export function aboutYouWithoutInvitedEmail(aboutYou: unknown): unknown {
  if (typeof aboutYou !== 'object' || aboutYou === null || Array.isArray(aboutYou)) return aboutYou;
  const { email: _email, ...rest } = aboutYou as Record<string, unknown>;
  return rest;
}

/** {@link aboutYouWithoutInvitedEmail} applied to a whole submission (a shallow copy). */
export function submissionWithoutInvitedEmail(submission: Record<string, unknown>): Record<string, unknown> {
  return { ...submission, aboutYou: aboutYouWithoutInvitedEmail(submission['aboutYou']) };
}

export const INTAKE_CASH_ACCOUNT_TYPES = ['checking', 'savings', 'money-market', 'cd', 'high-yield-savings'] as const;
export const INTAKE_INVESTMENT_ACCOUNT_TYPES = ['401k', 'ira', 'roth-ira', 'sep-ira', 'brokerage', 'annuity', 'other'] as const;
export const INTAKE_PROPERTY_TYPES = ['primary-residence', 'rental', 'commercial', 'vacation', 'land'] as const;
/** Real estate: is a rental occupied right now (RIPL "currently", 2026-10-07). */
export const INTAKE_RENTAL_OCCUPANCY = ['occupied', 'vacant'] as const;
/** Real estate: long-term plan for a rental or commercial property (RIPL, 2026-10-07). */
export const INTAKE_PROPERTY_PLANS = ['keep', 'sell-eventually', 'not-sure'] as const;
/** Real estate (commercial): does the client's own business rent the space back (RIPL, 2026-10-07). */
export const INTAKE_BUSINESS_RENTS_SPACE = ['yes', 'no-personal-use', 'mixed-use'] as const;
/** Real estate (vacation home): does it generate rental income (RIPL, 2026-10-07). */
export const INTAKE_VACATION_RENTAL_INCOME = ['yes-seasonally', 'yes-regularly', 'no'] as const;

/**
 * Which `realEstate` row fields the wizard asks for, per property type (RIPL's updated form,
 * 2026-10-07). The schema accepts every field on every row (all optional, so a tampered or
 * older client is never a 400 over a field that does not apply); this table is what the
 * wizard renders, what it submits, and what the advisor's document shows by default.
 * `propertyType`, `description` (location) and `estimatedValue` are asked of every type and
 * so are not listed. Land has no mortgage rate: RIPL asks only "amount owed".
 */
export const INTAKE_PROPERTY_FIELDS_BY_TYPE = {
  'primary-residence': ['mortgageBalance', 'mortgageRate'],
  'rental': ['mortgageBalance', 'mortgageRate', 'purchasePrice', 'purchaseYear', 'monthlyRent', 'occupancy', 'longTermPlan', 'improvements'],
  'commercial': ['mortgageBalance', 'businessRentsSpace', 'monthlyRent', 'longTermPlan'],
  'vacation': ['mortgageBalance', 'mortgageRate', 'vacationRentalIncome', 'annualRentalIncome'],
  'land': ['landWhatItIs', 'mortgageBalance', 'landPlan'],
} as const satisfies Record<(typeof INTAKE_PROPERTY_TYPES)[number], readonly string[]>;

/**
 * Whether a real-estate row asks `field` at all, given its property kind. ONE predicate: the
 * wizard renders from it, `toSubmission` sends from it, and `planPrefill` writes notes from
 * it. The advisor's document lists a field this asks, and also one it does not ask that holds
 * a stored answer (see `propertyFields` in `src/advisor/intakeDocument.ts`). `propertyType`, `description` and `estimatedValue`
 * are asked of every kind, including none chosen yet. Own-property lookup, so a stored kind of
 * `constructor` or `__proto__` asks nothing.
 */
export function propertyAsks(propertyType: unknown, field: string): boolean {
  if (field === 'propertyType' || field === 'description' || field === 'estimatedValue') return true;
  if (typeof propertyType !== 'string' || !Object.hasOwn(INTAKE_PROPERTY_FIELDS_BY_TYPE, propertyType)) return false;
  return (INTAKE_PROPERTY_FIELDS_BY_TYPE[propertyType as keyof typeof INTAKE_PROPERTY_FIELDS_BY_TYPE] as readonly string[])
    .includes(field);
}

/** The four "Getting started" questions: Yes or No, or left unanswered (the key is absent). */
export const INTAKE_YES_NO = ['yes', 'no'] as const;

/** Business: legal form (RIPL, 2026-10-07). */
export const INTAKE_ENTITY_TYPES = ['llc', 's-corp', 'c-corp', 'partnership', 'sole-proprietorship', 'other'] as const;
export const INTAKE_REVENUE_RANGES = ['under-500k', '500k-1m', '1m-5m', '5m-10m', '10m-25m', '25m-50m', '50m-100m', '100m-plus'] as const;
export const INTAKE_EMPLOYEE_COUNTS = ['just-me', '2-5', '6-20', '21-50', '51-100', '100-plus'] as const;
export const INTAKE_OWNER_PAY = ['salary', 'distributions', 'both', 'none'] as const;
export const INTAKE_OTHER_OWNERS = ['sole-owner', 'has-other-owners'] as const;
export const INTAKE_BUSINESS_HOLDINGS = ['none', 'real-estate', 'equipment', 'both'] as const;
export const INTAKE_EXIT_THINKING = [
  'not-yet', 'thinking-no-plan', 'rough-timeline', 'specific-within-5-years',
  'pass-to-family-or-partner', 'sell-to-employees-or-management', 'other',
] as const;
export const INTAKE_BUY_SELL = ['yes', 'no', 'not-sure'] as const;

/**
 * The two business "describe it" follow-ups only ask when the answer above them opens the
 * question. Shared by the wizard, `toSubmission`, the advisor's document and `planPrefill`.
 */
export const asksOtherOwnersDetails = (otherOwners: unknown): boolean => otherOwners === 'has-other-owners';
export const asksBusinessHoldingsDetails = (businessHoldings: unknown): boolean =>
  typeof businessHoldings === 'string' && (INTAKE_BUSINESS_HOLDINGS as readonly string[]).includes(businessHoldings)
  && businessHoldings !== 'none';
export const INTAKE_OTHER_ASSET_TYPES = ['crypto', 'collectibles', 'vehicles', 'private-placements', 'other'] as const;
export const INTAKE_DEBT_TYPES = ['loan', 'line-of-credit', 'credit-card', 'other'] as const;
/**
 * RIPL's own eleven policy types, in the order their form lists them (read from
 * riplwealth.com/snapshot on 2026-10-08; a founder decision of the same day to match it).
 */
export const INTAKE_POLICY_TYPES = [
  'term-life', 'whole-life', 'variable-life', 'universal-life', 'disability', 'long-term-care', 'umbrella',
  'business-overhead-expense', 'buy-sell', 'key-person', 'other',
] as const;
export const INTAKE_ESTATE_DOCUMENTS = ['will', 'revocable-trust', 'durable-poa', 'healthcare-directive', 'none-yet', 'not-sure'] as const;
/**
 * RIPL's own five checkboxes for "the primary job of the assets", in their order
 * (docs/evidence/ripl-intake-form/capture.json, captured 2026-08-21; a founder decision of
 * 2026-10-09 to match it). The last was two values here before that (`support-family`,
 * `charitable-giving`).
 */
export const INTAKE_ASSET_JOBS = ['grow-long-term', 'generate-income', 'preserve-capital', 'fund-goals', 'benefit-others'] as const;
/**
 * Investment Profile's five selects: RIPL's own options, in their order (read from
 * riplwealth.com/snapshot on 2026-10-08; a founder decision of the same day to match them).
 */
export const INTAKE_INCOME_TIMELINES = ['near-term', 'mid-term', 'no-current-need', 'depends-on-account'] as const;
export const INTAKE_MARKET_FAMILIARITY = ['new', 'some-experience', 'active'] as const;
export const INTAKE_RISK_CONCERNS = ['missing-growth', 'losing-ground', 'both'] as const;
export const INTAKE_DOWNTURN_RESPONSES = ['stay-the-course', 'reassess', 'reduce-risk'] as const;
export const INTAKE_CASH_NEED_TIMELINES = ['none', 'known-expense'] as const;
/** RIPL's own three options for "Select" on a child row (docs/evidence/ripl-intake-form §2), minus the neutral placeholder. */
export const INTAKE_CHILD_GENDERS = ['male', 'female', 'prefer-not-to-say'] as const;
/**
 * RIPL's per-advisor "How is that relationship?" select (docs/evidence/
 * ripl-intake-form §10), minus its "Select one" placeholder. Added to
 * {@link IntakeSubmissionShape}'s `currentTeam` rows alongside the existing free-text
 * `relationship` field rather than in place of it: ours asks how long the client has
 * worked with someone, RIPL's asks how they feel about them now — different questions,
 * both worth keeping.
 */
export const INTAKE_RELATIONSHIP_QUALITY = ['would-recommend', 'ok-for-now', 'need-new'] as const;

/**
 * The 13 wizard sections, in form order: Getting started first, Did we miss anything last
 * (docs/25 "Reference" section for the ones between, written when there were eleven).
 *
 * Exported separately from {@link IntakeSubmissionSchema} (the `.refine()`d version
 * below) because zod 4.4.3 refuses `.omit()` on a schema carrying a refinement —
 * `intakeMessage.ts` derives the queue-message shape from THIS one. Deriving from the
 * unrefined shape costs nothing: by the time a submission reaches the queue it has
 * already passed the refine at the façade (`handlers/intake.ts` parses with
 * {@link IntakeSubmissionSchema} before anything else touches the body), and a
 * honeypot-triggered submission never reaches the enqueue step at all, so the queue
 * message's payload is always already known non-empty.
 */
export const IntakeSubmissionShape = z.strictObject({
  /** Honeypot — see {@link INTAKE_HONEYPOT_FIELD}. Hidden in the UI. */
  [INTAKE_HONEYPOT_FIELD]: z.string().max(200).optional(),

  /**
   * 0 — Getting started (crossmarc/steward-app#2190). Four optional Yes/No questions, stored as
   * `'yes'` / `'no'` strings and not booleans: {@link isEmptyValue} reads `false` as blank, so a
   * boolean "No" would not count as an answer. Which sections the answers hide is decided in
   * `intakeRouting.ts`, nowhere else.
   */
  gettingStarted: z.strictObject({
    ownsRealEstate: z.enum(INTAKE_YES_NO).optional(),
    ownsBusiness: z.enum(INTAKE_YES_NO).optional(),
    businessHoldsAssets: z.enum(INTAKE_YES_NO).optional(),
    receivesRentalIncome: z.enum(INTAKE_YES_NO).optional(),
  }),

  /**
   * 1 — About You. Every field optional, matching RIPL's own form (docs/evidence/
   * ripl-intake-form) — not one control on any of its eleven sections is required,
   * and About You is no exception there either. Same shape as the spouse's copy.
   */
  aboutYou: person,

  /**
   * 2 — Spouse/Partner & children. Each child carries a real `dateOfBirth` rather
   * than a `birthYear` guess, and a `gender` select — both match fields RIPL's own
   * form asks per child (docs/evidence/ripl-intake-form §2) that an earlier partial
   * capture of that form never saw, because it never answered "Yes" to reveal them.
   * `dateOfBirth` stays a bounded STRING like every other date-ish field in this
   * schema (see the file header) — the one difference is the wizard renders it with
   * a native `<input type="date">`, since that is RIPL's own control for this field,
   * not a placeholder-driven free-text guess.
   */
  spousePartner: z.strictObject({
    hasSpousePartner: z.boolean(),
    spouse: person.optional(),
    children: z.array(z.strictObject({
      name: short.optional(),
      dateOfBirth: short.optional(),
      gender: z.enum(INTAKE_CHILD_GENDERS).optional(),
    })).max(MAX_INTAKE_ROWS),
  }),

  /**
   * 3 — Income & Financial Freedom. `notes` per income source (RIPL: "Pre-tax,
   * post-tax, variable, etc.") is distinct from the section-level `notes` below,
   * which is RIPL's separate freedom-goals textarea.
   */
  incomeAndGoals: z.strictObject({
    incomeSources: z.array(z.strictObject({
      description: short.optional(),
      annualAmount: amount.optional(),
      notes: short.optional(),
    })).max(MAX_INTAKE_ROWS),
    monthlyExpenses: amount.optional(),
    targetAnnualIncome: amount.optional(),
    notes: longText.optional(),
  }),

  /** 4 — Cash & Liquid Assets */
  cashAccounts: z.array(z.strictObject({
    /** The id the browser gave this row when it was added; statements uploaded into the row name it. Never content (`intakeRowId.ts`). (crossmarc/steward-app#2185) */
    rowId: IntakeRowIdSchema.optional(),
    accountType: z.enum(INTAKE_CASH_ACCOUNT_TYPES).optional(),
    institution: short.optional(),
    balance: amount.optional(),
  })).max(MAX_INTAKE_ROWS),

  /**
   * 5 — Investment & Retirement Accounts. `owner` ("Whose account" — RIPL: "e.g.
   * Mine, spouse, joint") was the one field this section was missing against
   * RIPL's own form. `statementsNote` has no RIPL counterpart at all — it is our
   * own addition, paired with the section's "please do not attach statements
   * here" copy, and stays.
   */
  investmentAccounts: z.strictObject({
    accounts: z.array(z.strictObject({
      /** The id the browser gave this row when it was added; statements uploaded into the row name it. Never content (`intakeRowId.ts`). (crossmarc/steward-app#2185) */
      rowId: IntakeRowIdSchema.optional(),
      accountType: z.enum(INTAKE_INVESTMENT_ACCOUNT_TYPES).optional(),
      institution: short.optional(),
      owner: short.optional(),
      balance: amount.optional(),
    })).max(MAX_INTAKE_ROWS),
    statementsNote: longText.optional(),
  }),

  /** 6 — Real Estate. `mortgageRate` (RIPL: "e.g. 3.25% or n/a") stays free text
   *  like every other rate/amount field in this schema. */
  realEstate: z.array(z.strictObject({
    propertyType: z.enum(INTAKE_PROPERTY_TYPES).optional(),
    description: short.optional(),
    estimatedValue: amount.optional(),
    mortgageBalance: amount.optional(),
    mortgageRate: amount.optional(),
    // Type-dependent fields (see INTAKE_PROPERTY_FIELDS_BY_TYPE).
    purchasePrice: amount.optional(),
    purchaseYear: short.optional(),
    /** Monthly rent collected (rental) or received (commercial). */
    monthlyRent: amount.optional(),
    occupancy: z.enum(INTAKE_RENTAL_OCCUPANCY).optional(),
    longTermPlan: z.enum(INTAKE_PROPERTY_PLANS).optional(),
    improvements: longText.optional(),
    businessRentsSpace: z.enum(INTAKE_BUSINESS_RENTS_SPACE).optional(),
    vacationRentalIncome: z.enum(INTAKE_VACATION_RENTAL_INCOME).optional(),
    annualRentalIncome: amount.optional(),
    /** Land: what the parcel is. */
    landWhatItIs: short.optional(),
    /** Land: what the client plans to do with it. */
    landPlan: longText.optional(),
  })).max(MAX_INTAKE_ROWS),

  /**
   * 7 — Business Interests. `ownershipPercentage`, `lastValuationDate` and
   * `completedBy` are the three fields RIPL asks per business that this section
   * was missing entirely. `description` ("What it does, and your part in it")
   * has no RIPL counterpart — our own addition, kept for the context it gives an
   * advisor reading a one-line business name cold.
   */
  businessInterests: z.array(z.strictObject({
    name: short.optional(),
    description: longText.optional(),
    estimatedValue: amount.optional(),
    ownershipPercentage: amount.optional(),
    lastValuationDate: short.optional(),
    completedBy: short.optional(),
    // The deeper questions on RIPL's updated form (2026-10-07).
    entityType: z.enum(INTAKE_ENTITY_TYPES).optional(),
    annualRevenue: z.enum(INTAKE_REVENUE_RANGES).optional(),
    employeeCount: z.enum(INTAKE_EMPLOYEE_COUNTS).optional(),
    ownerPay: z.enum(INTAKE_OWNER_PAY).optional(),
    annualTakeHome: amount.optional(),
    otherOwners: z.enum(INTAKE_OTHER_OWNERS).optional(),
    /** Asked only when `otherOwners` is `has-other-owners`: names, ownership %, roles. */
    otherOwnersDetails: longText.optional(),
    businessHoldings: z.enum(INTAKE_BUSINESS_HOLDINGS).optional(),
    /** Asked only when `businessHoldings` is not `none`. */
    businessHoldingsDetails: longText.optional(),
    exitThinking: z.enum(INTAKE_EXIT_THINKING).optional(),
    buySellAgreement: z.enum(INTAKE_BUY_SELL).optional(),
    /** "If something happened to you tomorrow, what happens to the business?" */
    ifSomethingHappened: longText.optional(),
  })).max(MAX_INTAKE_ROWS),

  /**
   * 8 — Other Assets & Debt. RIPL's "Other assets" rows are just type + value;
   * `description` ("What it is") is our own addition with no RIPL counterpart,
   * kept for the same reason as business interests' `description`. Debts gain
   * `interestRate` and `monthlyPayment`, the two fields RIPL asks per debt that
   * were missing here.
   */
  otherAssetsAndDebt: z.strictObject({
    otherAssets: z.array(z.strictObject({
      assetType: z.enum(INTAKE_OTHER_ASSET_TYPES).optional(),
      description: short.optional(),
      estimatedValue: amount.optional(),
    })).max(MAX_INTAKE_ROWS),
    debts: z.array(z.strictObject({
      debtType: z.enum(INTAKE_DEBT_TYPES).optional(),
      description: short.optional(),
      balance: amount.optional(),
      interestRate: amount.optional(),
      monthlyPayment: amount.optional(),
    })).max(MAX_INTAKE_ROWS),
  }),

  /** 9 — Insurance & Estate */
  insuranceAndEstate: z.strictObject({
    policies: z.array(z.strictObject({
      policyType: z.enum(INTAKE_POLICY_TYPES).optional(),
      provider: short.optional(),
      coverageAmount: amount.optional(),
    })).max(MAX_INTAKE_ROWS),
    estateDocuments: z.array(z.enum(INTAKE_ESTATE_DOCUMENTS)).max(INTAKE_ESTATE_DOCUMENTS.length)
      .refine(uniqueItems, 'duplicate selections'),
    lastReviewed: short.optional(),
  }),

  /**
   * 10 — Current Team. CPA and attorney are the form's named slots; further
   * advisors are "+ Add advisor" rows. `relationship` stays a bounded free-text
   * string ("How long you have worked together") — our own addition, not RIPL's.
   * `relationshipQuality` is new: RIPL's own "How is that relationship?" select
   * ({@link INTAKE_RELATIONSHIP_QUALITY}) on every one of its team rows, which an
   * earlier pass deliberately left unmodelled (see the field-spec's now-superseded
   * note on this). Both fields are kept side by side — see the constant's comment.
   */
  currentTeam: z.strictObject({
    cpa: z.strictObject({
      name: short.optional(), firm: short.optional(), relationship: short.optional(),
      relationshipQuality: z.enum(INTAKE_RELATIONSHIP_QUALITY).optional(),
    }).optional(),
    attorney: z.strictObject({
      name: short.optional(), firm: short.optional(), relationship: short.optional(),
      relationshipQuality: z.enum(INTAKE_RELATIONSHIP_QUALITY).optional(),
    }).optional(),
    otherAdvisors: z.array(z.strictObject({
      name: short.optional(),
      firm: short.optional(),
      role: short.optional(),
      relationshipQuality: z.enum(INTAKE_RELATIONSHIP_QUALITY).optional(),
    })).max(MAX_INTAKE_ROWS),
    notes: longText.optional(),
  }),

  /** 11 — Investment Profile */
  investmentProfile: z.strictObject({
    assetJobs: z.array(z.enum(INTAKE_ASSET_JOBS)).max(INTAKE_ASSET_JOBS.length)
      .refine(uniqueItems, 'duplicate selections'),
    incomeTimeline: z.enum(INTAKE_INCOME_TIMELINES).optional(),
    marketFamiliarity: z.enum(INTAKE_MARKET_FAMILIARITY).optional(),
    riskConcern: z.enum(INTAKE_RISK_CONCERNS).optional(),
    downturnResponse: z.enum(INTAKE_DOWNTURN_RESPONSES).optional(),
    upcomingCashNeeds: z.enum(INTAKE_CASH_NEED_TIMELINES).optional(),
    philosophy: longText.optional(),
  }),

  /** 12 — Did we miss anything: one free-text field, bounded like the other long answers. */
  anythingElse: z.strictObject({
    notes: longText.optional(),
  }),
});

/** {@link IntakeSubmissionShape} plus the one guard described on {@link isSubmissionEmpty}. */
export const IntakeSubmissionSchema = IntakeSubmissionShape.refine((data) => !isSubmissionEmpty(data), {
  message: 'This submission has no answers to send.',
});

export type IntakeSubmission = z.infer<typeof IntakeSubmissionSchema>;

/**
 * Firm-side workflow state on a stored `advisor-intake` row (docs/26 §2b,
 * steward-app#740). **Not part of the submission**, and deliberately declared here
 * rather than inside {@link IntakeSubmissionShape}: a family never sends it, the
 * public route never accepts it, and `IntakeSubmissionSchema` staying `strictObject`
 * is what guarantees that. It is a top-level sibling of `intake` on the entity, so it
 * cannot collide with anything the family wrote.
 *
 * Three values and no more. Enum-pinned so an unknown one is a 400 at the façade
 * rather than a string that quietly becomes a fourth column in the portal.
 */
export const INTAKE_STATUSES = ['new', 'reviewed', 'archived'] as const;

/** The write route's whole vocabulary — `PUT /intakes/{id}/status`. */
export const IntakeStatusSchema = z.enum(INTAKE_STATUSES);

export type IntakeStatus = (typeof INTAKE_STATUSES)[number];

/** What the writer stamps on a brand-new submission, and what a row stored before
 *  this field existed reads as. One constant so the writer's default and the
 *  façade's read-time default cannot drift apart. */
export const INTAKE_DEFAULT_STATUS: IntakeStatus = 'new';

/**
 * The read-time default (steward-app#740). Rows written before this field existed
 * carry no `status`, and there is no backfill: no operator can currently run a data
 * migration against appcore's `/system/*` plane (steward-app#813), a read-time
 * default is idempotent and needs no per-environment ceremony, and the population is
 * small. Anything that is not one of the three known values normalises to `new`
 * rather than reaching the portal as an unrenderable string.
 */
export function normalizeIntakeStatus(value: unknown): IntakeStatus {
  return INTAKE_STATUSES.includes(value as IntakeStatus) ? value as IntakeStatus : INTAKE_DEFAULT_STATUS;
}
